/*
 * PRD-037 — Primitivos UI del portal del partner (app SEPARADA).
 * Diseño V4: reusa los componentes CANÓNICOS del panel (`@components/v4/*`) — Button, FieldV4 +
 * TextInputV4 — en vez de un fork hecho a mano. Los componentes V4 son inline-style puros (sin
 * Tailwind) y no requieren providers; el ThemeProvider de @plick/ui que sí necesitan GeneralInfo /
 * ScheduleManager se monta una sola vez en el root (PartnerApp). PageShell/Card/Title/Badge siguen
 * siendo wrappers locales sobre los tokens reales `V4`.
 */
import type { CSSProperties, ReactNode } from 'react';
import { V4 } from '@components/v4/tokens';
import { Button as ButtonV4, type ButtonVariant } from '@components/v4/Button/Button';
import { FieldV4, TextInputV4 } from '@components/v4/Input/Input';
import { useDesktop } from '../utils/hooks/useDesktop';

export const C = {
  primary: V4.primary,
  secondary: V4.secondary,
  secondaryDark: V4.secondaryDark,
  secondaryLight: V4.secondaryLight,
  success: V4.success,
  danger: V4.danger,
  warning: V4.warning,
  white: V4.white,
  pageBg: V4.grayBlueLight, // #F2F6F8
  cardBorder: V4.grayLight, // #E5ECEE
  muted: V4.grayUltraDark, // #6B6B6B
  inputBorder: V4.grayLight, // #E5ECEE — canon: border de input en reposo
};

const FONT = 'Poppins, sans-serif';

// DESIGN.md: los labels de campo terminan en ":". Lo aplicamos aquí para no repetirlo en cada page.
const withColon = (label: string) => (label.trim().endsWith(':') ? label : `${label}:`);

export const PageShell = ({ children, narrow }: { children: ReactNode; narrow?: boolean }) => {
  const isDesktop = useDesktop();
  return (
    <div
      style={{
        minHeight: '100vh',
        background: C.pageBg,
        fontFamily: FONT,
        color: C.primary,
        padding: isDesktop ? '24px 16px' : '16px 12px',
      }}
    >
      <div style={{ width: '100%', maxWidth: narrow ? 460 : 760, margin: '0 auto' }}>{children}</div>
    </div>
  );
};

export const Card = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => {
  const isDesktop = useDesktop();
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.cardBorder}`,
        borderRadius: 12,
        padding: isDesktop ? 24 : 16,
        marginBottom: 16,
        boxSizing: 'border-box',
        // minWidth:0 deja que la card encoja dentro de un flex/grid padre sin desbordar;
        // overflowWrap evita que un token largo (email, URL) empuje el ancho de la card.
        minWidth: 0,
        overflowWrap: 'anywhere',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export const Title = ({ children, sub }: { children: ReactNode; sub?: ReactNode }) => (
  <div style={{ marginBottom: sub ? 18 : 14 }}>
    <h1
      style={{
        fontSize: 22,
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
        margin: 0,
        // Un nombre de negocio largo sin espacios (o un email en un título) no debe empujar
        // el ancho de la página en móvil: siempre puede romper línea.
        minWidth: 0,
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </h1>
    {sub ? <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>{sub}</p> : null}
  </div>
);

export const SectionTitle = ({ children }: { children: ReactNode }) => (
  <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 14px' }}>{children}</h2>
);

// El partner reusa el Button canónico V4. El "ghost" del partner mapea a `outline`
// (white / primary / borde gray) — NUNCA al ghost canónico (transparent, sin borde),
// que se reservaría para acciones sobre fondos coloreados.
const VARIANT_MAP: Record<'primary' | 'secondary' | 'ghost', ButtonVariant> = {
  primary: 'primary',
  secondary: 'secondary',
  ghost: 'outline',
};

type ButtonProps = {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  style?: CSSProperties; // solo transporta margen/layout del consumidor
  children?: ReactNode;
};

export const Button = ({ variant = 'primary', style, fullWidth, children, ...rest }: ButtonProps) => {
  const btn = (
    <ButtonV4 variant={VARIANT_MAP[variant]} fullWidth={fullWidth} {...rest}>
      {children}
    </ButtonV4>
  );
  if (!style) return btn;
  // El Button canónico no expone `style` (protege sus tokens). El margen/posicionamiento del
  // consumidor vive en un wrapper para no romper esa disciplina.
  return (
    <span style={{ display: fullWidth ? 'block' : 'inline-flex', width: fullWidth ? '100%' : undefined, ...style }}>
      {btn}
    </span>
  );
};

type InputModeType = 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';

type FieldProps = {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  error?: string;
  autoComplete?: string;
  inputMode?: InputModeType;
};

export const Field = ({
  label,
  value,
  onChange,
  placeholder,
  type,
  required,
  error,
  autoComplete,
  inputMode,
}: FieldProps) => (
  <div style={{ marginBottom: 14 }}>
    <FieldV4 label={withColon(label)} required={required} error={error}>
      <TextInputV4
        value={value}
        onChange={(v) => onChange(String(v))}
        placeholder={placeholder}
        type={type}
        autoComplete={autoComplete}
        inputMode={inputMode}
      />
    </FieldV4>
  </div>
);

// Campo de WhatsApp con lada +52 fija y captura de 10 dígitos nacionales (espeja el registro).
type PhoneFieldProps = {
  label: string;
  value: string; // solo los 10 dígitos nacionales
  onChange: (digits: string) => void;
  error?: string | null;
  required?: boolean;
};
export const PhoneField = ({ label, value, onChange, error, required }: PhoneFieldProps) => (
  <div style={{ marginBottom: 14 }}>
    <FieldV4 label={withColon(label)} required={required} error={error ?? undefined}>
      <TextInputV4
        value={value}
        onChange={(v) => onChange(String(v).replace(/\D/g, '').slice(0, 10))}
        placeholder="10 dígitos"
        inputMode="numeric"
        prefix="+52"
      />
    </FieldV4>
  </div>
);

// Pill de estado V4 (patrón STATUS_CFG de DESIGN.md): punto de color + label, fondo suave.
export const Badge = ({ tone, children }: { tone: 'draft' | 'claimed' | 'pending'; children: ReactNode }) => {
  const color = tone === 'claimed' ? V4.successDark : tone === 'pending' ? V4.warningDark : V4.secondaryDark;
  const background = tone === 'claimed' ? V4.successFaded : tone === 'pending' ? V4.warningFaded : V4.secondaryFaded;
  const dot = tone === 'claimed' ? V4.success : tone === 'pending' ? V4.warningDark : V4.secondary;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
        padding: '3px 10px 3px 8px',
        borderRadius: 9999,
        whiteSpace: 'nowrap',
        color,
        background,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 9999,
          flexShrink: 0,
          background: dot,
        }}
      />
      {children}
    </span>
  );
};

// Fila de ficha (label/valor) responsive. Causa raíz del bug del email cortado: el patrón previo
// usaba un label con `width` fija y un valor SIN min-width:0 ni word-break, así que un token largo
// (email/URL) desbordaba la card. Aquí el valor SIEMPRE puede romper línea (overflowWrap:anywhere)
// y en móvil la fila se apila (label arriba en gris pequeño, valor abajo a ancho completo).
export const InfoRow = ({ label, value, first }: { label: string; value: ReactNode; first?: boolean }) => {
  const isDesktop = useDesktop();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isDesktop ? 'row' : 'column',
        alignItems: isDesktop ? 'baseline' : 'stretch',
        gap: isDesktop ? 16 : 3,
        padding: isDesktop ? '11px 0' : '10px 0',
        borderTop: first ? 'none' : `1px solid ${C.cardBorder}`,
        minWidth: 0,
      }}
    >
      <span
        style={{
          flex: isDesktop ? '0 0 150px' : undefined,
          fontSize: isDesktop ? 13 : 11,
          fontWeight: 600,
          color: C.muted,
          textTransform: isDesktop ? undefined : 'uppercase',
          letterSpacing: isDesktop ? undefined : '0.04em',
          lineHeight: 1.3,
        }}
      >
        {label}
      </span>
      <span
        style={{
          flex: isDesktop ? 1 : undefined,
          minWidth: 0,
          fontSize: 14,
          fontWeight: 500,
          color: C.primary,
          lineHeight: 1.45,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {value || '—'}
      </span>
    </div>
  );
};

export const ErrorNote = ({ children }: { children: ReactNode }) => (
  <p
    style={{
      color: C.danger,
      fontSize: 13,
      margin: '12px 0 0',
      background: V4.dangerFaded,
      padding: '8px 12px',
      borderRadius: 6,
    }}
  >
    {children}
  </p>
);

export const Muted = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <p style={{ fontSize: 13, color: C.muted, margin: '8px 0 0', ...style }}>{children}</p>
);
